import { useEffect } from "react";

import { usePersistentState } from "@/contexts/LocalStorage";
import { useNotificationsContext } from "@/contexts/Notification";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { USER_NOTIFICATION_FIELDS } from "@/graphql/notifications/types";
import { gql, useQuery } from "@apollo/client";

import ClinicalPrinciplesAutoimmunityPromo from "../ui/ClinicalPrinciplesAutoimmunityPromo";
import LinkYourBankAccount from "../ui/LinkYourBankAccount";
import NotificationModal from "../ui/NotificationModal";
import PlannedSystemMaintenance from "../ui/PlannedSystemMaintenance";

const CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID =
  "clinical-principles-autoimmunity-promo-per-session-notification";

const ON_CMP_LOGIN_QUERY = gql`
  query OnCmpLoginQuery($organisation: ID!) {
    onCmpLogin(organisation: $organisation) {
      ...UserNotificationFields
    }
  }
  ${USER_NOTIFICATION_FIELDS}
`;

export default function OnCmpLogin() {
  /**
   * Event: when the partner logs in to the cmp for the first time, or the app loads with the user
   * logged in already for the first time
   */

  const api = useNotificationsContext();
  const { organisation } = useOrganisationContext();

  const { data } = useQuery(ON_CMP_LOGIN_QUERY, {
    variables: { organisation: organisation?.id },
    skip: !organisation?.id
  });

  const [
    showClinicalPrinciplesAutoimmunityPromoNotification,
    setShowClinicalPrinciplesAutoimmunityPromoNotification
  ] = usePersistentState("ClinicalPrinciplesAutoimmunityPromoNotification", true);

  useEffect(() => {
    if (data) {
      const notifications = [
        {
          id: CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID,
          notification: {
            id: CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID,
            type: CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID
          }
        }
      ];

      api.addNotifications([...notifications, ...data.onCmpLogin]);
    }
  }, [api, data]);

  const userNotification = api.nextNotification(un => {
    if (
      [
        "cmp.UserMissingLinkedBankAccount",
        "accounts.PlannedSystemMaintenance",
        CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID
      ].includes(un.notification.type)
    ) {
      if (un?.notification?.type === CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID) {
        if (!showClinicalPrinciplesAutoimmunityPromoNotification) return false;
      }
      return true;
    }
    return false;
  });

  return userNotification ? (
    <NotificationModal
      onClose={() => {
        if (
          userNotification.notification.id !==
          CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID
        ) {
          api.acknowledgeNotification(userNotification.id);
        } else {
          api.setAcknowledged(acknowledged => {
            const newAcknowledged = new Set(acknowledged);
            newAcknowledged.add(userNotification.id);
            return newAcknowledged;
          });
        }
      }}
    >
      {userNotification.notification.type === "cmp.UserMissingLinkedBankAccount" && (
        <LinkYourBankAccount />
      )}
      {userNotification.notification.type === "accounts.PlannedSystemMaintenance" && (
        <PlannedSystemMaintenance
          from={userNotification.notification.validFrom}
          to={userNotification.notification.validTo}
        />
      )}
      {userNotification.notification.type ===
        CLINICAL_PRINCIPLES_AUTOIMMUNITY_PROMO_NOTIFICATION_ID && (
        <ClinicalPrinciplesAutoimmunityPromo
          setShowNotification={setShowClinicalPrinciplesAutoimmunityPromoNotification}
          onClose={() => {
            api.setAcknowledged(acknowledged => {
              const newAcknowledged = new Set(acknowledged);
              newAcknowledged.add(userNotification.id);
              return newAcknowledged;
            });
          }}
        />
      )}
    </NotificationModal>
  ) : null;
}
